import React, { useState, useEffect, ForwardedRef, useCallback } from 'react';

import styled, { StyledProps } from 'styled-components';

import { ScrollContainerProps, ShadowScrollContainerProps } from './ShadowScrollContainer.decl';

import { isScrollAtBottom, isScrollAtTop } from '@aircall/tractor-v2';
import { forwardRefWithGenericType } from '@dashboard/library';
import { useCombinedRefs } from '@hooks/useCombinedRefs/useCombinedRefs';

function makeBorderBottom({ theme, hasBottomBorder }: StyledProps<ScrollContainerProps>) {
  return hasBottomBorder && `border-bottom: 1px solid ${theme.colors.grey.base};`;
}

export const ScrollContainer = styled.div<ScrollContainerProps>`
  overflow: auto;
  padding: 0;
  margin: 0;
  list-style-type: none;
  ${(props) => `max-height: ${props.maxHeight}px`};
  ${({ hasBottomShadow, hasTopShadow }) => {
    const shadows: string[] = [
      ...(hasTopShadow ? ['inset 0 19px 20px -31px grey'] : []),
      ...(hasBottomShadow ? ['inset 0 -19px 20px -31px grey'] : []),
    ];
    return `box-shadow: ${shadows.join(',')};`;
  }}
  ${makeBorderBottom}
  transition: 500ms box-shadow;
`;

const ROW_HEIGHT = 41;
const NUMBER_OF_ROWS_DISPLAYED = 9;

export function ShadowScrollContainerInner<TElement extends HTMLElement = HTMLDivElement>(
  {
    children,
    maxHeight = ROW_HEIGHT * NUMBER_OF_ROWS_DISPLAYED + ROW_HEIGHT / 2,
    hasBottomBorder = false,
    forceShadow = false,
    'data-test': dataTest = 'scroll-container',
    offsetTop = 0,
    as = 'div',
    ...otherProps
  }: ShadowScrollContainerProps,
  ref: ForwardedRef<TElement>
) {
  const totalMaxHeight = offsetTop + maxHeight;
  const localRef = React.useRef<TElement>(null);
  const combinedRef = useCombinedRefs<TElement>(localRef, ref);
  const [hasBottomShadow, setBottomShadow] = useState<boolean>(false);
  const [hasTopShadow, setTopShadow] = useState<boolean>(false);

  const setScrollShadow = useCallback((el: TElement) => {
    const atBottom = isScrollAtBottom<TElement>(el);
    const atTop = isScrollAtTop<TElement>(el);
    setBottomShadow(!atBottom);
    setTopShadow(!atTop);
  }, []);

  // Set scroll shadow when component renders because scroll event has not been triggered yet
  useEffect(() => {
    setScrollShadow(localRef.current as TElement);
  }, [localRef, setScrollShadow]);

  const handleScroll = useCallback(
    (e: React.UIEvent<TElement>) => {
      setScrollShadow(e.currentTarget);
    },
    [setScrollShadow]
  );

  return (
    <ScrollContainer
      as={as}
      ref={combinedRef}
      data-test={dataTest}
      maxHeight={totalMaxHeight}
      onScroll={handleScroll}
      hasTopShadow={hasTopShadow}
      hasBottomShadow={forceShadow || hasBottomShadow}
      hasBottomBorder={hasBottomBorder}
      {...otherProps}
    >
      {children}
    </ScrollContainer>
  );
}

export const ShadowScrollContainer = forwardRefWithGenericType(ShadowScrollContainerInner);
